import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { Heading, Substyle } from "common/core/typography";
import Button from "common/core/button";
import { pushNotification } from "common/core/notification_center/actions";
import { captureException } from "util/exception";
import { StyledTextInput } from "common/core/form/text";
import { useForm } from "common/core/form";
import { FieldErrorMessage, isAriaInvalid } from "common/core/form/error";
import LoadingIndicator from "common/core/loading_indicator";
import { useMutation, useQuery } from "util/graphql";
import CopyButton from "common/core/copy_button";
import { useActiveOrganization } from "common/account/active_organization";
import { IconButton } from "common/core/button/icon_button";
import WorkflowModal from "common/modals/workflow_modal";
import AlertMessage from "common/core/alert_message";
import { b } from "util/html";

import Styles from "./view.module.scss";
import GetOAuthApplicationsQuery, {
  type GetOAuthApplications_organization_Organization as Organization,
} from "./get_oauth_applications.graphql";
import UpdateOAuthApplicationMutation from "./update_oauth_application_mutation.graphql";
import GenerateOAuthApplicationSecretMutation from "./generate_oauth_application_secret_mutation.graphql";
import DeleteOAuthApplicationSecretMutation from "./delete_oauth_application_secret_mutation.graphql";

const MESSAGES = defineMessages({
  applicationId: {
    id: "a94b272f-fe36-46f3-aabe-ccdbeaf663b0",
    defaultMessage: "App ID",
  },
  clientSecrets: {
    id: "0bae2be2-edb5-41cc-922c-6fcd27f46af7",
    defaultMessage: "Client Secrets",
  },
  updateNameButton: {
    id: "f6b66101-c424-455c-ba7c-622d4adaf558",
    defaultMessage: "Update",
  },
  secret: {
    id: "93911e21-3f69-4751-a337-aa630f3b053d",
    defaultMessage: "Secret",
  },
  nameFieldLabel: {
    id: "744d19dc-7698-4200-a687-857a7d138fd5",
    defaultMessage: "Display name",
  },
  nameRequiredMessage: {
    id: "b438fba6-663d-4f3e-bebf-3863476d44be",
    defaultMessage: "Client application name is required",
  },
  nameFieldMaxLength: {
    id: "def4e7a6-ae68-480c-882f-2c6cac5276c7",
    defaultMessage: "Client application name is too long",
  },
  copyToClipboard: {
    id: "f01d937b-cd3d-47f6-a045-c474e863a642",
    defaultMessage: "Copy to clipboard",
  },
  updateSuccessMessage: {
    id: "2cf83605-eba5-40f4-898e-bcc614c3a7d9",
    defaultMessage: "Application name updated",
  },
  updateFailureMessage: {
    id: "f3f60c87-5e86-4001-8b55-bd3f859ae769",
    defaultMessage: "Failed to update application name",
  },
  clientSecretNote: {
    id: "70c8564a-09b2-474a-b25a-94a60b1021cf",
    defaultMessage:
      "Make sure to copy your new client secret now. You won’t be able to see it again.",
  },
  noDeletionMessage: {
    id: "44d5201d-43a6-438a-9a20-10d566fecaa5",
    defaultMessage: "You can't delete this secret if it's the only one.",
  },
  generatedOn: {
    id: "2bb9c99a-08ca-44ab-8e02-87d7203c5431",
    defaultMessage: "Generated on {date}",
  },
  createdOnDateTime: {
    id: "f95654a8-f6fc-4ccd-b1d3-976ed7f77fec",
    defaultMessage: "Created on {date} at {time}",
  },
  cancel: {
    id: "2883ba7e-7f86-455c-81d4-96cac09c61f3",
    defaultMessage: "Cancel",
  },
  saveChanges: {
    id: "e8d3c2c5-0b9f-4a8f-8a0e-7c6f2d1f3c7b",
    defaultMessage: "Save changes",
  },
  generateNewSecret: {
    id: "c006f6f2-3739-4c3c-b0c2-b54cca374f16",
    defaultMessage: "Generate new client secret",
  },
  generateSecretSuccessMessage: {
    id: "8783261f-9146-4b06-a0fe-39607590408c",
    defaultMessage: "New client secret generated",
  },
  generateSecretFailureMessage: {
    id: "9973110b-bc15-4f52-a94d-4b3be27888db",
    defaultMessage: "Failed to generate new client secret",
  },
  deleteSecretButton: {
    id: "07e1d1a8-d4e8-4f33-9bb8-6acede3d24ab",
    defaultMessage: "Delete secret",
  },
  deleteSecretSuccessMessage: {
    id: "43f11e39-7c27-4bfe-ad59-f7c0c192a0e2",
    defaultMessage: "Client secret deleted",
  },
  deleteSecretFailureMessage: {
    id: "41f06bf3-d040-486c-988b-731da1bcd8e5",
    defaultMessage: "Failed to delete client secret",
  },
  deleteSecretModalTitle: {
    id: "b6210566-410c-4210-9ca5-3a18b9addb5c",
    defaultMessage: "Delete secret {secret} ?",
  },
  deleteSecretModalWarning: {
    id: "3369f1e7-7ac2-44a6-bdab-699aaa31b4d9",
    defaultMessage: "This action cannot be undone.",
  },
  confirmDeleteButton: {
    id: "786135c2-c574-4c3e-9dd8-b9dcacd356e8",
    defaultMessage: "I understand the consequences, revoke secret",
  },
});

type LocationState = {
  newClientSecret?: {
    plaintextSecretValue: string;
    clientSecretId: string;
    createdAt: string;
  };
};

export function OAuthApplicationView() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ applicationId: string }>();
  const applicationId = params.applicationId!;

  const newClientSecret = (location.state as LocationState | undefined)?.newClientSecret;

  const [organizationId] = useActiveOrganization();
  const updateOauthApplication = useMutation(UpdateOAuthApplicationMutation);
  const generateOauthApplicationSecretMutateFn = useMutation(
    GenerateOAuthApplicationSecretMutation,
  );
  const deleteOauthApplicationSecretMutateFn = useMutation(DeleteOAuthApplicationSecretMutation);

  type FormValues = {
    displayName: string;
  };

  const form = useForm<FormValues>({
    defaultValues: {
      displayName: "",
    },
  });

  const {
    formState: { errors },
    reset,
  } = form;

  const { data, loading } = useQuery(GetOAuthApplicationsQuery, {
    variables: { organizationId: organizationId as string },
  });

  const organization = data?.organization as Organization | null;
  const applications = organization?.oauthApplications || [];
  const application = useMemo(
    () => applications.find((app) => app.id === applicationId)!,
    [applications, applicationId],
  );

  useEffect(() => {
    if (!loading) {
      reset({ displayName: application.name });
    }
  }, [application, reset]);

  useEffect(() => {
    if (newClientSecret) {
      const seenSecretFlag = `seen-secret-${newClientSecret.clientSecretId}`;

      if (sessionStorage.getItem(seenSecretFlag)) {
        navigate(location.pathname, {
          replace: true,
          state: {},
        });
        sessionStorage.removeItem(seenSecretFlag);
      } else {
        sessionStorage.setItem(seenSecretFlag, "true");
      }
    }
  }, [newClientSecret, location.pathname, navigate]);

  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [isGeneratingSecret, setIsGeneratingSecret] = useState(false);
  const [deletingSecretId, setDeletingSecretId] = useState<string | null>(null);
  const [secretPendingDelete, setSecretPendingDelete] = useState<{
    id: string;
    value: string;
  } | null>(null);

  const handleUpdateName = useCallback(
    (formValues: FormValues) => {
      setIsUpdatingName(true);

      return updateOauthApplication({
        variables: {
          input: { id: applicationId, name: formValues.displayName },
        },
      })
        .then(() => {
          pushNotification({
            subtype: "SUCCESS",
            message: intl.formatMessage(MESSAGES.updateSuccessMessage),
          });
        })
        .catch((error: Error) => {
          captureException(error);
          pushNotification({
            subtype: "ERROR",
            message: intl.formatMessage(MESSAGES.updateFailureMessage),
          });
        })
        .finally(() => {
          setIsUpdatingName(false);
        });
    },
    [applicationId, updateOauthApplication, intl],
  );

  const handleGenerateSecret = useCallback(async () => {
    setIsGeneratingSecret(true);
    try {
      const response = await generateOauthApplicationSecretMutateFn({
        variables: { input: { id: applicationId } },
      });

      const secretData = response.data!.generateOAuthApplicationSecret!;

      const { plaintextSecretValue, clientSecretId, oauthApplication } = secretData;

      const newSecretDetails = oauthApplication.oauthApplicationSecrets.find(
        (secret) => secret.id === clientSecretId,
      );

      const newSecretForState = {
        plaintextSecretValue,
        clientSecretId,
        createdAt: newSecretDetails?.createdAt,
      };

      navigate(location.pathname, { replace: true, state: { newClientSecret: newSecretForState } });

      pushNotification({
        subtype: "SUCCESS",
        message: intl.formatMessage(MESSAGES.generateSecretSuccessMessage),
      });
    } catch (error) {
      captureException(error);
      pushNotification({
        subtype: "ERROR",
        message: intl.formatMessage(MESSAGES.generateSecretFailureMessage),
      });
    } finally {
      setIsGeneratingSecret(false);
    }
  }, [applicationId, generateOauthApplicationSecretMutateFn, navigate, location.pathname, intl]);

  const handleDeleteSecret = useCallback(
    async (secretId: string) => {
      setDeletingSecretId(secretId);
      try {
        await deleteOauthApplicationSecretMutateFn({
          variables: {
            input: {
              organizationId: organizationId as string,
              oauthApplicationId: applicationId,
              clientSecretId: secretId,
            },
          },

          refetchQueries: [
            {
              query: GetOAuthApplicationsQuery,
              variables: { organizationId },
            },
          ],
        });

        if (newClientSecret?.clientSecretId === secretId) {
          navigate(location.pathname, { replace: true, state: {} });
          sessionStorage.removeItem(`seen-secret-${secretId}`);
        }

        pushNotification({
          subtype: "SUCCESS",
          message: intl.formatMessage(MESSAGES.deleteSecretSuccessMessage),
        });
      } catch (error) {
        captureException(error as Error);
        pushNotification({
          subtype: "ERROR",
          message: intl.formatMessage(MESSAGES.deleteSecretFailureMessage),
        });
      } finally {
        setDeletingSecretId(null);
      }
    },
    [
      applicationId,
      organizationId,
      deleteOauthApplicationSecretMutateFn,
      intl,
      pushNotification,
      newClientSecret,
    ],
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.appHeaderInfo}>
        <Heading level="h2" textStyle="headingThree">
          {application.name}
        </Heading>
        <div>
          <FormattedMessage
            id="9364590e-712e-480c-8f13-a04529eb5bc1"
            defaultMessage="<b>Created:</b> {date} at {time}"
            values={{
              b,
              date: intl.formatDate(application.createdAt, {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              }),
              time: intl.formatDate(application.createdAt, {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              }),
            }}
          />
        </div>
        <div>
          <FormattedMessage
            id="d6288272-9b4d-4d9d-bb24-274e8f4b5dfd"
            defaultMessage="<b>App token expiration time:</b> Every 2 hours"
            values={{
              b,
            }}
          />
        </div>
      </div>

      <div className={Styles.contentSection}>
        <div className={Styles.applicationNameSection}>
          <form onSubmit={form.handleSubmit(handleUpdateName)}>
            <div className={Styles.nameEditForm}>
              <StyledTextInput
                data-automation-id="display-name-input"
                aria-invalid={isAriaInvalid(errors.displayName)}
                disabled={isUpdatingName}
                label={intl.formatMessage(MESSAGES.nameFieldLabel)}
                displayRequiredAsterisk
                {...form.register("displayName", {
                  required: true,
                  maxLength: {
                    value: 256,
                    message: intl.formatMessage(MESSAGES.nameFieldMaxLength),
                  },
                })}
              />
              <div>
                <Button
                  type="submit"
                  buttonColor="action"
                  variant="primary"
                  isLoading={isUpdatingName}
                  buttonSize="large"
                  disabled={form.watch("displayName") === application.name}
                >
                  {intl.formatMessage(MESSAGES.saveChanges)}
                </Button>
              </div>
            </div>
            {errors.displayName && (
              <div>
                <FieldErrorMessage
                  inputName="displayName"
                  message={
                    errors.displayName.message || intl.formatMessage(MESSAGES.nameRequiredMessage)
                  }
                />
              </div>
            )}
          </form>
        </div>

        <div className={Styles.applicationDetails}>
          <div>{intl.formatMessage(MESSAGES.applicationId)}</div>
          <div>{application.id}</div>
        </div>

        {application.oauthApplicationSecrets.length > 0 && (
          <div>
            {application.oauthApplicationSecrets
              .filter((secret) => !newClientSecret || secret.id !== newClientSecret.clientSecretId)
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
              .map((secret) => {
                return (
                  <div key={secret.id} className={Styles.secretsList}>
                    <div>{intl.formatMessage(MESSAGES.secret)}</div>
                    <Substyle size="small" display="block">
                      {intl.formatMessage(MESSAGES.createdOnDateTime, {
                        date: intl.formatDate(secret.createdAt, {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                        }),
                        time: intl.formatDate(secret.createdAt, {
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        }),
                      })}
                    </Substyle>
                    <div className={Styles.secretRow}>
                      <StyledTextInput
                        aria-invalid={false}
                        disabled
                        readOnly
                        value={secret.obfuscatedSecret}
                        className={Styles.secretValue}
                      />
                      {application.oauthApplicationSecrets.length > 1 && (
                        <IconButton
                          name="trash"
                          variant="tertiary"
                          buttonColor="danger"
                          label={intl.formatMessage(MESSAGES.deleteSecretButton)}
                          onClick={() =>
                            setSecretPendingDelete({
                              id: secret.id,
                              value: secret.obfuscatedSecret,
                            })
                          }
                          isLoading={deletingSecretId === secret.id}
                        />
                      )}
                    </div>
                    {application.oauthApplicationSecrets.length === 1 && (
                      <Substyle textColor="subtle" size="small" display="block">
                        {intl.formatMessage(MESSAGES.noDeletionMessage)}
                      </Substyle>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {newClientSecret && (
          <>
            <div>{intl.formatMessage(MESSAGES.secret)}</div>
            <Substyle size="small" display="block">
              {intl.formatMessage(MESSAGES.createdOnDateTime, {
                date: intl.formatDate(newClientSecret.createdAt, {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                }),
                time: intl.formatDate(newClientSecret.createdAt, {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                }),
              })}
            </Substyle>
            <div className={Styles.secretRow}>
              <StyledTextInput
                aria-invalid={false}
                disabled
                readOnly
                value={newClientSecret.plaintextSecretValue}
                className={Styles.secretValue}
              />
              <div className={Styles.secretActions}>
                {application.oauthApplicationSecrets.length > 1 && (
                  <IconButton
                    name="trash"
                    variant="tertiary"
                    buttonColor="danger"
                    label={intl.formatMessage(MESSAGES.deleteSecretButton)}
                    onClick={() =>
                      setSecretPendingDelete({
                        id: newClientSecret.clientSecretId,
                        value: newClientSecret.plaintextSecretValue,
                      })
                    }
                    isLoading={deletingSecretId === newClientSecret.clientSecretId}
                  />
                )}
                <CopyButton
                  variant="secondary"
                  buttonColor="action"
                  value={newClientSecret.plaintextSecretValue}
                  automationId="copy-record-button"
                  buttonSize="large"
                >
                  {intl.formatMessage(MESSAGES.copyToClipboard)}
                </CopyButton>
              </div>
            </div>
            <Substyle textColor="subtle" size="small" display="block">
              {intl.formatMessage(MESSAGES.clientSecretNote)}
            </Substyle>
          </>
        )}

        <div className={Styles.generateButton}>
          <Button
            onClick={handleGenerateSecret}
            buttonColor="action"
            variant="primary"
            isLoading={isGeneratingSecret}
            disabled={isGeneratingSecret || isUpdatingName}
          >
            {intl.formatMessage(MESSAGES.generateNewSecret)}
          </Button>
        </div>
      </div>
      {secretPendingDelete && (
        <WorkflowModal
          autoFocus
          positionTop
          large
          title={intl.formatMessage(MESSAGES.deleteSecretModalTitle, {
            secret: secretPendingDelete.value,
          })}
          buttons={[
            <Button
              key="cancel"
              buttonColor="dark"
              variant="tertiary"
              onClick={() => setSecretPendingDelete(null)}
            >
              {intl.formatMessage(MESSAGES.cancel)}
            </Button>,
            <Button
              key="confirm"
              buttonColor="danger"
              variant="primary"
              isLoading={deletingSecretId === secretPendingDelete.id}
              onClick={() =>
                handleDeleteSecret(secretPendingDelete.id).finally(() =>
                  setSecretPendingDelete(null),
                )
              }
            >
              {intl.formatMessage(MESSAGES.confirmDeleteButton)}
            </Button>,
          ]}
          footerSeparator={false}
        >
          <AlertMessage kind="danger">
            {intl.formatMessage(MESSAGES.deleteSecretModalWarning)}
          </AlertMessage>
        </WorkflowModal>
      )}
    </div>
  );
}
